import React, { useCallback, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  DollarSign,
  Calendar,
  ChevronRight,
  CheckCircle,
  Loader2,
  TrendingUp,
  Wallet,
  Clock,
  Zap,
  ArrowRight,
  Sparkles,
  Percent,
  HandCoins,
  Target,
  X,
} from "lucide-react";

import LayoutAuth from "blocks/layout-auth/layout-auth";
import { MainStore } from "../../stores/Main";
import API from "../../utils/api";
import history from "../../utils/history";
import { formatMoney } from "../../utils/formatMoney";
import useModal from "../../hooks/use-modal";
import { usePostHog } from "posthog-js/react";
import Select from "react-select";
import AccountInfoBar from "../../components/account-info-bar/account-info-bar";

const PaymentOptions = (props) => {
  const [accounts, setAccounts] = useState([]);
  const [accountIdx, setAccountIdx] = useState(null);
  const { openModal, modalTypes } = useModal();
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [ready, setReady] = useState(false);
  const posthog = usePostHog();
  const { appConfig } = useContext(MainStore);
  const [freedeCredit, setFreedeCredit] = useState(null);
  const [freedeCreditDismissed, setFreedeCreditDismissed] = useState(
    () => localStorage.getItem("freede_credit_dismissed") === "true"
  );

  // Animation variants
  const containerAnimation = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const cardHoverAnimation = {
    scale: 1.02,
    transition: { duration: 0.2 },
  };

  const fetchPaymentPlans = useCallback(
    async (_, accs) => {
      try {
        console.log(
          "[payment-options] fetchPaymentPlans called, accountIdx:",
          accountIdx,
          "account.id:",
          accs[accountIdx]?.value
        );
        setReady(false);
        console.log(
          "[payment-options] Making API call to /payment_plans/config"
        );
        const response = await API.get(
          "/accounts/" + accs[accountIdx].value + "/payment_plans/config"
        );
        if (response.data) {
          // Store the full payment config for eligibility checks
          // Ensure single_eligible is explicitly boolean (false if not exactly true)
          setPaymentConfig({
            ...response.data,
            single_eligible: response.data.single_eligible === true,
          });
          console.log(
            "[payment-options] Payment config received:",
            response.data
          );
          setReady(true);
        }
      } catch (e) {
        console.log(e);
        setReady(true);
      }
    },
    [accountIdx]
  );

  const { userType } = useContext(MainStore);

  const doSkip = useCallback(() => {
    posthog?.capture("skip_payment_options");
    if (userType === "consumer-quick-access") {
      props.history.push("/dashboard/accounts");
    } else if (appConfig.onboarding_sentiment !== "disabled") {
      props.history.push("/sentiment-questionnaire");
    }
  }, [appConfig, posthog, props.history, userType]);

  const checkForFreedeCredit = async (accountList) => {
    console.log(
      "[FreedeCredit] Checking for credit, dismissed:",
      freedeCreditDismissed,
      "accounts:",
      accountList.length
    );
    if (freedeCreditDismissed) {
      console.log("[FreedeCredit] Skipping - already dismissed");
      return;
    }

    try {
      for (const account of accountList) {
        console.log(
          "[FreedeCredit] Fetching adjustments for account:",
          account.value
        );
        const response = await API.get(
          `/accounts/${account.value}/adjustments`
        );
        console.log("[FreedeCredit] Adjustments response:", response.data);
        if (!response.data.result_code && response.data.adjustments) {
          const credit = response.data.adjustments.find(
            (adj) => adj.payment_memo_code === "FREEDEREGCREDIT"
          );
          if (credit) {
            console.log("[FreedeCredit] Found FREEDEREGCREDIT:", credit);
            setFreedeCredit(credit);
            break;
          }
        }
      }
    } catch (e) {
      console.log("[FreedeCredit] Error:", e);
    }
  };

  const dismissFreedeCredit = () => {
    localStorage.setItem("freede_credit_dismissed", "true");
    setFreedeCreditDismissed(true);
    setFreedeCredit(null);
  };

  const fetchAccounts = async () => {
    try {
      const response = await API.get("/accounts");
      const accountList = [];
      if (!response.data.result_code) {
        response.data.account.forEach((account, idx) => {
          if (
            account.status === undefined ||
            account.status === 0 ||
            account.status === 1
          ) {
            // Keep the full account data for the AccountInfoBar
            const accountName =
              account.chargeoff_creditor?.name ||
              account.creditor?.name ||
              "Unknown Account";
            const refId =
              account.chargeoff_creditor?.ref_id ||
              account.creditor?.ref_id ||
              account.id;
            accountList.push({
              idx,
              value: account.id,
              // label stays the flat string: react-select filters on it, and
              // it's the accessible name. Rendering uses formatOptionLabel.
              label:
                accountName +
                " #" +
                refId +
                " (Balance: $" +
                formatMoney(account.balance_current) +
                ")",
              creditorName: accountName,
              refId,
              balance: account.balance_current,
              creditor_id: account.creditor.id,
              creditor: account.creditor,
              name: account.name,
              balance_current: account.balance_current,
              balance_start: account.balance_start,
              id: account.id,
              // Include all account data for the AccountInfoBar
              agency: account.agency,
              chargeoff_creditor: account.chargeoff_creditor,
              balance_buckets: account.balance_buckets,
              type: account.type,
              status: account.status,
            });
          }
        });
        // If no accounts, skip to dashboard
        if (accountList.length === 0) {
          history.push("/");
        } else {
          setAccounts(accountList);
          // Auto-select if only one account
          if (accountList.length === 1) {
            setAccountIdx(0);
          }
          // Check for Freede registration credit
          checkForFreedeCredit(accountList);
          return accountList;
        }
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (accountIdx === null) return;
    if (accounts.length > 0) {
      fetchPaymentPlans(accounts[accountIdx].creditor_id, accounts);
    }
  }, [accounts, accountIdx, fetchPaymentPlans]);

  // Calculate example payment plan for preview
  const getExamplePaymentPlan = () => {
    if (
      !paymentConfig ||
      !paymentConfig.eligible ||
      !paymentConfig.max_months ||
      accountIdx === null
    ) {
      return null;
    }
    const cur_bal = Number(accounts[accountIdx].balance);
    // Show the lowest monthly payment (using max months)
    const monthlyPayment = cur_bal / paymentConfig.max_months;
    return {
      months: paymentConfig.max_months,
      monthly: monthlyPayment,
    };
  };

  // Rich two-line account row, used for both the dropdown options and the
  // selected value (react-select renders this instead of the flat label; the
  // flat label is still what search filters and screen readers use).
  const formatAccountOption = (opt) => (
    <div className="flex items-center justify-between gap-3 w-full min-w-0">
      <div className="min-w-0">
        <div className="text-[15px] font-medium text-white truncate">
          {opt.creditorName}
        </div>
        <div className="text-xs text-gray-400 truncate">
          Account #{opt.refId}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-[15px] font-semibold text-white tabular-nums">
          ${formatMoney(opt.balance_current)}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500">
          Balance
        </div>
      </div>
    </div>
  );

  // Dark theme select styles
  const darkSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused ? "2px solid #3b82f6" : "2px solid #4b5563",
      borderRadius: "12px",
      backgroundColor: "rgba(31, 41, 55, 0.9)",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      minHeight: "48px",
      "&:hover": {
        borderColor: "#3b82f6",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#e5e7eb",
      fontWeight: "500",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "12px",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
      backgroundColor: "#1f2937",
      border: "1px solid #374151",
      overflow: "hidden",
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "6px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgba(59, 130, 246, 0.25)"
        : state.isFocused
          ? "#374151"
          : "transparent",
      color: "#e5e7eb",
      cursor: "pointer",
      margin: "2px 0",
      padding: "10px 12px",
      borderRadius: "10px",
      border: state.isSelected
        ? "1px solid rgba(59, 130, 246, 0.6)"
        : "1px solid transparent",
      "&:active": {
        backgroundColor: "rgba(59, 130, 246, 0.35)",
      },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#9ca3af",
      "&:hover": {
        color: "#e5e7eb",
      },
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: "#4b5563",
    }),
  };

  return (
    <LayoutAuth showIllustration={false} showContainer={false}>
      <div className="max-w-6xl mx-auto px-3 py-3 sm:px-6 sm:py-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 sm:mb-8"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-3 sm:p-6 lg:p-8 text-white mx-1 sm:mx-0">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                    <CreditCard size={20} className="sm:hidden" />
                    <CreditCard size={24} className="hidden sm:block" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold">
                    Payment Options
                  </h1>
                </div>
                <p className="text-blue-100 text-sm sm:text-base lg:text-lg">
                  Choose the payment plan that works best for your financial
                  situation
                </p>
              </div>
              <div className="hidden lg:block">
                <Sparkles className="w-24 h-24 text-blue-200/30" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Freede Registration Credit Banner */}
        {freedeCredit && !freedeCreditDismissed && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 mx-1 sm:mx-0"
          >
            <div className="bg-green-500/20 border border-green-500/40 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/30 rounded-lg flex items-center justify-center">
                  <CheckCircle size={20} className="text-green-400" />
                </div>
                <p className="text-green-300 font-semibold">
                  Your Freede Enrollment Balanace Reduction Credit has been
                  successfully applied!
                </p>
              </div>
              <button
                onClick={dismissFreedeCredit}
                className="text-green-400 hover:text-green-300 p-1"
                aria-label="Dismiss"
              >
                <X size={20} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Loading State */}
        {accounts.length === 0 && !ready && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-16"
          >
            <div className="relative">
              <div className="w-20 h-20 border-4 border-gray-700 border-t-blue-500 rounded-full animate-spin" />
              <Wallet
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-600"
                size={32}
              />
            </div>
            <p className="mt-4 text-gray-400">Loading your accounts...</p>
          </motion.div>
        )}

        {/* Account Selection (only show if more than 1 account) */}
        {accounts.length > 1 && (
          <motion.div
            variants={containerAnimation}
            initial="hidden"
            animate="show"
            className="space-y-6"
          >
            <motion.div
              variants={itemAnimation}
              className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6 mb-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Wallet size={20} className="text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    Select Account
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Choose an account to view payment options
                  </p>
                </div>
              </div>

              <Select
                id="select-account"
                placeholder="Select an account to continue..."
                options={accounts}
                value={accounts[accountIdx]}
                onChange={(selected) => {
                  setAccountIdx(selected.idx);
                }}
                formatOptionLabel={formatAccountOption}
                isSearchable={false}
                styles={darkSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </motion.div>
          </motion.div>
        )}

        {/* Loading payment options */}
        {accountIdx !== null && !ready && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center py-8"
          >
            <div className="flex items-center gap-3 text-gray-400">
              <Loader2 className="animate-spin" size={20} />
              <span>Loading payment options...</span>
            </div>
          </motion.div>
        )}

        {/* Payment Options */}
        {ready && accountIdx !== null && (
          <motion.div
            variants={containerAnimation}
            initial="hidden"
            animate="show"
            className="space-y-3 sm:space-y-6"
          >
            {/* Account Info Bar with expandable details */}
            <div className="px-1 sm:px-0">
              <AccountInfoBar account={accounts[accountIdx]} />
            </div>

            {/* Payment Option Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 lg:gap-4 px-1 sm:px-0">
              {/* Pay in Full Option - Only shown when single_eligible */}
              {paymentConfig && paymentConfig.single_eligible && (
                <motion.div
                  variants={itemAnimation}
                  whileHover={cardHoverAnimation}
                  onClick={() =>
                    openModal(modalTypes.ModalOffer, {
                      account: accounts[accountIdx],
                      mode: "one-time",
                      offers: paymentConfig,
                      prefillAmount: true, // This is for Pay in Full
                    })
                  }
                  className="relative overflow-hidden cursor-pointer group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl" />
                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-1 z-10">
                    <Zap size={12} />
                    Fastest
                  </div>
                  <div className="relative h-full bg-gradient-to-r from-blue-600/90 to-blue-700/90 backdrop-blur rounded-lg sm:rounded-xl lg:rounded-2xl border-2 border-blue-500/50 p-3 sm:p-4 lg:p-6 hover:border-blue-400 transition-all flex flex-col">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 lg:mb-4">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white/20 backdrop-blur rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Zap size={18} className="text-white sm:hidden" />
                        <Zap
                          size={20}
                          className="text-white hidden sm:block lg:hidden"
                        />
                        <Zap size={24} className="text-white hidden lg:block" />
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white">
                          Pay in Full
                        </h3>
                        <p className="text-blue-100 text-xs sm:text-sm hidden sm:block">
                          Clear your balance immediately
                        </p>
                      </div>
                    </div>
                    <div className="mb-2 sm:mb-3 lg:mb-4 flex-grow">
                      <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
                        ${formatMoney(accounts[accountIdx].balance)}
                      </p>
                      <p className="text-blue-100 text-xs sm:text-sm">
                        One-time payment
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-300" />
                        <span className="text-blue-100 text-xs sm:text-sm">
                          Immediate resolution
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-white">
                        <span className="text-xs sm:text-sm font-medium">
                          View Options
                        </span>
                        <ArrowRight
                          className="group-hover:translate-x-2 transition-transform"
                          size={20}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Payment Plan Option */}
              {paymentConfig &&
                paymentConfig.eligible &&
                paymentConfig.max_months && (
                  <motion.div
                    variants={itemAnimation}
                    whileHover={cardHoverAnimation}
                    onClick={() => {
                      openModal(modalTypes.ModalOffer, {
                        account: accounts[accountIdx],
                        mode: "payment-plan",
                        offers: paymentConfig,
                      });
                    }}
                    className="relative overflow-hidden cursor-pointer group h-full"
                  >
                    <div className="bg-gradient-to-r from-green-600/10 to-emerald-600/10 rounded-lg sm:rounded-xl lg:rounded-2xl border-2 border-green-500/30 p-3 sm:p-4 lg:p-6 hover:border-green-400/50 transition-all h-full flex flex-col">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 lg:mb-4">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-green-500/20 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Calendar
                            size={18}
                            className="text-green-400 sm:hidden"
                          />
                          <Calendar
                            size={20}
                            className="text-green-400 hidden sm:block lg:hidden"
                          />
                          <Calendar
                            size={24}
                            className="text-green-400 hidden lg:block"
                          />
                        </div>
                        <div>
                          <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white">
                            Payment Plan
                          </h3>
                          <p className="text-gray-400 text-xs sm:text-sm hidden sm:block">
                            Spread payments over time
                          </p>
                        </div>
                      </div>

                      <div className="flex-grow">
                        {getExamplePaymentPlan() && (
                          <div className="mb-2 sm:mb-3 lg:mb-4">
                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-400">
                              As low as $
                              {formatMoney(getExamplePaymentPlan().monthly)}
                            </p>
                            <p className="text-gray-400 text-xs sm:text-sm">
                              /month for {getExamplePaymentPlan().months} months
                            </p>
                          </div>
                        )}

                        <div className="bg-green-500/10 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3 lg:mb-4 hidden sm:block">
                          <p className="text-green-300 text-xs sm:text-sm">
                            <Target size={14} className="inline mr-1" />
                            Click to see all available terms
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <TrendingUp size={16} className="text-green-400" />
                          <span className="text-gray-300 text-xs sm:text-sm">
                            Flexible options
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-green-400">
                          <span className="text-xs sm:text-sm font-medium">
                            Choose Terms
                          </span>
                          <ArrowRight
                            className="group-hover:translate-x-2 transition-transform"
                            size={20}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

              {/* Resolution Offer Option */}
              {paymentConfig && paymentConfig.offer_eligible && (
                <motion.div
                  variants={itemAnimation}
                  whileHover={cardHoverAnimation}
                  onClick={() => {
                    openModal(modalTypes.ModalOffer, {
                      account: accounts[accountIdx],
                      mode: "settlement-offer",
                      offers: paymentConfig,
                    });
                  }}
                  className="relative overflow-hidden cursor-pointer group h-full"
                >
                  <div className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 rounded-lg sm:rounded-xl lg:rounded-2xl border-2 border-purple-500/30 p-3 sm:p-4 lg:p-6 hover:border-purple-400/50 transition-all h-full flex flex-col">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 lg:mb-4">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-purple-500/20 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Percent
                          size={18}
                          className="text-purple-400 sm:hidden"
                        />
                        <Percent
                          size={20}
                          className="text-purple-400 hidden sm:block lg:hidden"
                        />
                        <Percent
                          size={24}
                          className="text-purple-400 hidden lg:block"
                        />
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white">
                          Resolution Offer
                        </h3>
                        <p className="text-gray-400 text-xs sm:text-sm hidden sm:block">
                          Resolve for less than full amount
                        </p>
                      </div>
                    </div>

                    <div className="flex-grow">
                      <div className="mb-2 sm:mb-3 lg:mb-4">
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-400">
                          Make an Offer
                        </p>
                        <p className="text-gray-400 text-xs sm:text-sm">
                          Potential savings available
                        </p>
                      </div>

                      <div className="bg-purple-500/10 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3 lg:mb-4 hidden sm:block">
                        <p className="text-purple-300 text-xs sm:text-sm">
                          <HandCoins size={14} className="inline mr-1" />
                          Negotiate a resolution amount
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles size={16} className="text-purple-400" />
                        <span className="text-gray-300 text-xs sm:text-sm">
                          Save money
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-purple-400">
                        <span className="text-xs sm:text-sm font-medium">
                          Make Offer
                        </span>
                        <ArrowRight
                          className="group-hover:translate-x-2 transition-transform"
                          size={20}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Custom Payment Option */}
              {paymentConfig && paymentConfig.single_eligible && (
                <motion.div
                  variants={itemAnimation}
                  whileHover={cardHoverAnimation}
                  onClick={() => {
                    openModal(modalTypes.ModalOffer, {
                      account: accounts[accountIdx],
                      mode: "one-time",
                      offers: paymentConfig,
                      prefillAmount: false, // This is for Custom Payment
                    });
                  }}
                  className="relative overflow-hidden cursor-pointer group h-full"
                >
                  <div className="bg-gray-800/50 backdrop-blur rounded-lg sm:rounded-xl lg:rounded-2xl border-2 border-gray-600 p-3 sm:p-4 lg:p-6 hover:border-gray-500 transition-all h-full flex flex-col">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 lg:mb-4">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gray-700/50 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <DollarSign
                          size={18}
                          className="text-gray-400 sm:hidden"
                        />
                        <DollarSign
                          size={20}
                          className="text-gray-400 hidden sm:block lg:hidden"
                        />
                        <DollarSign
                          size={24}
                          className="text-gray-400 hidden lg:block"
                        />
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white">
                          Custom Payment
                        </h3>
                        <p className="text-gray-400 text-xs sm:text-sm hidden sm:block">
                          Pay what you can afford
                        </p>
                      </div>
                    </div>

                    <div className="flex-grow">
                      <div className="mb-2 sm:mb-3 lg:mb-4">
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-300">
                          Choose Amount
                        </p>
                        {paymentConfig.min_payment > 0 && (
                          <p className="text-gray-500 text-sm">
                            Min: ${formatMoney(paymentConfig.min_payment)}
                          </p>
                        )}
                      </div>

                      <div className="bg-gray-700/30 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3 lg:mb-4 hidden sm:block">
                        <p className="text-gray-400 text-sm">
                          Start with any comfortable amount
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-gray-400" />
                        <span className="text-gray-300 text-xs sm:text-sm">
                          Flexible amount
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <span className="text-xs sm:text-sm font-medium">
                          Enter Amount
                        </span>
                        <ArrowRight
                          className="group-hover:translate-x-2 transition-transform"
                          size={20}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Skip Option */}
            <motion.div
              variants={itemAnimation}
              className="flex justify-center pt-4"
            >
              <button
                onClick={doSkip}
                className="text-gray-400 hover:text-gray-300 text-sm font-medium transition-colors flex items-center gap-2 group"
              >
                <span>Maybe Later</span>
                <ChevronRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </LayoutAuth>
  );
};

export default PaymentOptions;
